/**
 * SocialPulse.blog sub-pages — News, Places, Publications, Writers.
 *
 * All share the BlogHome header/footer so navigation feels seamless.
 * Data comes from existing publisher_articles + locations collections;
 * empty states encourage the reader to become a writer.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, ArrowRight, Newspaper, Users, BookMarked, Compass,
  TrendingUp, Sparkles, PenLine,
} from "lucide-react";
import BlogHome from "./BlogHome";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

// Compact wrapper that re-uses BlogHome's header + footer by rendering
// them inline. Consumers pass a page body + optional pageTitle.
function BlogPage({ children, testid }) {
  const navigate = useNavigate();
  // Re-export the header/footer from BlogHome via a small trick: mount
  // an invisible BlogHome instance and pluck its slots. Simpler path:
  // duplicate the wrapper. Keeps sub-pages self-contained.
  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }} data-testid={testid}>
      {/* Reuse BlogHome as the chrome — it's already gated by hostname/route */}
      <div style={{ display: "contents" }}>
        {/* Render a slim replica header here so page state stays isolated */}
        <SlimHeader onNavigate={navigate} />
        <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-16">
          {children}
        </main>
        <SlimFooter />
      </div>
    </div>
  );
}

function SlimHeader({ onNavigate }) {
  return (
    <header className="border-b sticky top-0 z-20" style={{ borderColor: "rgba(15,118,110,0.08)", background: "#FAFAF7" }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center gap-6">
        <a href="/blog" className="flex items-center gap-3 shrink-0" data-testid="blog-page-logo">
          <span className="w-10 h-10 rounded-full grid place-items-center" style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}>
            <Compass className="w-5 h-5 text-white" />
          </span>
          <span className="hidden md:inline text-xl" style={{ fontFamily: "'Lora', serif", fontWeight: 700 }}>
            SocialPulse<sup className="text-xs" style={{ color: "#0F766E" }}>®</sup> <span style={{ color: "#0F766E" }}>Blog</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-6 flex-1 ml-4">
          <a href="/blog/news" className="text-[15px] font-semibold" style={{ color: "#0F766E" }}>News</a>
          <a href="/blog/places" className="text-[15px] text-slate-700 hover:text-slate-950">Places</a>
          <a href="/blog/topics" className="text-[15px] text-slate-700 hover:text-slate-950">Topics</a>
          <a href="/blog/questions" className="text-[15px] text-slate-700 hover:text-slate-950">Questions</a>
          <a href="/blog/publications" className="text-[15px] text-slate-700 hover:text-slate-950">Publications</a>
          <a href="/blog/writers" className="text-[15px] text-slate-700 hover:text-slate-950">Writers</a>
        </nav>
        <button
          onClick={() => onNavigate("/publisher/write")}
          className="rounded-full px-4 py-2 text-[13px] font-semibold text-white ml-auto"
          style={{ background: "#0F766E" }}
          data-testid="blog-page-write"
        >
          Start Writing
        </button>
      </div>
    </header>
  );
}

function SlimFooter() {
  return (
    <footer className="border-t py-8 text-center text-xs text-slate-400" style={{ borderColor: "rgba(15,23,42,0.05)" }}>
      © {new Date().getFullYear()} SocialPulse — Every Place Has a Story. Every Subject Has a Community.
    </footer>
  );
}

function PageHead({ overline, title, subtitle }) {
  return (
    <div className="mb-8">
      <p className="text-[11px] tracking-[0.24em] text-slate-500 uppercase mb-3">{overline}</p>
      <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "clamp(2rem,4vw,3rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
        {title}
      </h1>
      {subtitle && <p className="mt-3 text-slate-600 text-lg max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function ArticleCard({ a }) {
  return (
    <a href={`/blog/${a.slug}`} className="block bg-white rounded-2xl overflow-hidden border hover:shadow-md transition-shadow" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid={`blog-card-${a.slug}`}>
      {a.cover_image_url && (
        <div className="aspect-[16/10] overflow-hidden">
          <img src={a.cover_image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        {a.primary_location_name && (
          <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
            <MapPin className="w-3 h-3" style={{ color: "#f59e0b" }} />
            {a.primary_location_name}
          </p>
        )}
        <h3 className="text-lg text-slate-900 leading-snug" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>{a.title}</h3>
        {a.summary && <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{a.summary}</p>}
      </div>
    </a>
  );
}


// ─── /blog/news ──────────────────────────────────────────────────

const NEWS_CATEGORIES = ["local-news", "community", "business", "health", "education", "nature"];

export function BlogNews() {
  const [streams, setStreams] = useState({});
  useEffect(() => {
    Promise.all(NEWS_CATEGORIES.map(async (cat) => {
      const r = await fetch(`${BASE}/articles?category=${cat}&limit=4`);
      if (r.ok) return [cat, await r.json()];
      return [cat, { articles: [] }];
    })).then((entries) => setStreams(Object.fromEntries(entries)));
  }, []);
  return (
    <BlogPage testid="blog-news-page">
      <PageHead
        overline="News across the network"
        title="What the world is writing about today"
        subtitle="Community-reported news from every place and topic on SocialPulse. See something missing? Write it yourself."
      />
      <div className="mb-8 flex items-center gap-3 rounded-2xl bg-white p-4 border" style={{ borderColor: "rgba(15,118,110,0.15)" }}>
        <PenLine className="w-5 h-5 shrink-0" style={{ color: "#0F766E" }} />
        <p className="text-sm text-slate-700 flex-1">
          Everyone can write news. Add local reports, hyperlocal alerts, community wins, or a story you saw firsthand.
        </p>
        <a href="/publisher/write?category=local-news" className="rounded-full px-4 py-1.5 text-xs font-semibold text-white shrink-0" style={{ background: "#0F766E" }}>
          Write news
        </a>
      </div>
      <div className="space-y-10">
        {NEWS_CATEGORIES.map((cat) => {
          const list = streams[cat]?.articles || [];
          return (
            <section key={cat} data-testid={`blog-news-stream-${cat}`}>
              <div className="flex items-end justify-between mb-4">
                <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>
                  {cat.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </h2>
                <a href={`/topic/${cat}`} className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: "#0F766E" }}>
                  See all <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              {list.length === 0 ? (
                <div className="text-sm text-slate-500 bg-white rounded-xl p-4 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
                  No stories yet for this stream. <a href="/publisher/write" className="font-semibold" style={{ color: "#0F766E" }}>Be the first to write one →</a>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {list.slice(0, 4).map((a) => <ArticleCard key={a.id} a={a} />)}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </BlogPage>
  );
}


// ─── /blog/places ────────────────────────────────────────────────

const TRENDING_PLACES = [
  { name: "Kerala, India",         id: "india/kerala" },
  { name: "Portland, Oregon",      id: "united-states/oregon/portland" },
  { name: "Kyoto, Japan",          id: "japan/kyoto" },
  { name: "Cape Town, South Africa", id: "south-africa/cape-town" },
  { name: "Rio de Janeiro, Brazil",  id: "brazil/rio-de-janeiro" },
  { name: "Copenhagen, Denmark",     id: "denmark/copenhagen" },
];

export function BlogPlaces() {
  const [homeCity, setHomeCity] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("sp_home_city") || window.localStorage.getItem("np_home_city");
      if (raw) setHomeCity(JSON.parse(raw));
    } catch { /* noop */ }
    fetch(`${BASE}/articles?limit=8`).then((r) => r.ok && r.json()).then((d) => setRecent(d?.articles || []));
  }, []);

  return (
    <BlogPage testid="blog-places-page">
      <PageHead
        overline="Explore places"
        title={homeCity ? `Stories near ${homeCity.city || homeCity.name}` : "Trending places right now"}
        subtitle="Follow a city, a district, or a whole country. Every place has a story worth telling."
      />
      {homeCity && (
        <div className="mb-8 flex items-center gap-3 rounded-2xl bg-white p-4 border" style={{ borderColor: "rgba(15,118,110,0.15)" }}>
          <MapPin className="w-5 h-5 shrink-0" style={{ color: "#0F766E" }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700">
              Your saved location: <strong>{homeCity.city || homeCity.name}{homeCity.state ? `, ${homeCity.state}` : ""}</strong>
            </p>
          </div>
          <a href="/local" className="text-xs font-semibold" style={{ color: "#0F766E" }}>
            Change ›
          </a>
        </div>
      )}
      <section className="mb-12" data-testid="blog-trending-places">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>Trending places</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TRENDING_PLACES.map((p) => (
            <a key={p.id} href={`/place/${p.id}`} className="bg-white rounded-2xl p-5 border hover:shadow-md transition-shadow" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid={`blog-place-${p.id.replace(/\//g,'-')}`}>
              <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1"><MapPin className="w-3 h-3" style={{ color: "#f59e0b" }} /> Place</p>
              <h3 className="text-lg" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>{p.name}</h3>
              <p className="mt-1 text-sm inline-flex items-center gap-1 font-semibold" style={{ color: "#0F766E" }}>
                Read stories <ArrowRight className="w-4 h-4" />
              </p>
            </a>
          ))}
        </div>
      </section>
      <section data-testid="blog-latest-articles">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>Latest from every place</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {recent.slice(0, 8).map((a) => <ArticleCard key={a.id} a={a} />)}
          {recent.length === 0 && (
            <div className="col-span-full text-sm text-slate-500 bg-white rounded-xl p-4 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
              No stories yet. <a href="/publisher/write" className="font-semibold" style={{ color: "#0F766E" }}>Publish the first one →</a>
            </div>
          )}
        </div>
      </section>
    </BlogPage>
  );
}


// ─── /blog/publications ──────────────────────────────────────────

export function BlogPublications() {
  return (
    <BlogPage testid="blog-publications-page">
      <PageHead
        overline="Publications"
        title="Give your community a voice"
        subtitle="Publications are shared brands where writers, editors and communities publish together — like a magazine you can start in five minutes."
      />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <BookMarked className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Start a publication</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Portland Community Pulse. Kerala Heritage Stories. Farmers of the World. Anyone can create a publication for a city, school, family or subject.
          </p>
          <a href="/publisher/write" className="mt-4 inline-flex items-center gap-1 font-semibold" style={{ color: "#0F766E" }}>
            Create a publication <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <Sparkles className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Prominent publications</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Featured community publications curated by SocialPulse editors will appear here. Nominate a great local publication to be linked.
          </p>
          <p className="mt-3 text-[11px] text-slate-400 uppercase tracking-widest">Coming soon</p>
        </div>
      </div>
    </BlogPage>
  );
}


// ─── /blog/writers ───────────────────────────────────────────────

// ─── /blog/topics ────────────────────────────────────────────────

// Curated topics with per-topic identity: icon, color, invitation copy.
// Chosen to match the network's vertical presence — Farmers, Alumni,
// Family Trees, Memorials, etc. sit alongside broad publishing topics.
const TOPIC_CARDS = [
  { slug: "health",          label: "Health",              icon: "❤",  color: "#DC2626", tint: "#FEF2F2",
    blurb: "Share what a diagnosis, a doctor, a recipe or a walk taught you. Real stories change how people take care of themselves." },
  { slug: "education",       label: "Education",           icon: "📚", color: "#2563EB", tint: "#EFF6FF",
    blurb: "From village schools to global classrooms — write about a teacher, a course, a study hack, or a small experiment." },
  { slug: "finance",         label: "Finance",             icon: "💰", color: "#F59E0B", tint: "#FFFBEB",
    blurb: "Money, savings, taxes, first jobs, tiny businesses. What did nobody tell you that you had to learn the hard way?" },
  { slug: "food",            label: "Food",                icon: "🍲", color: "#EA580C", tint: "#FFF7ED",
    blurb: "Grandma's recipe, a street stall, a farm-to-table experiment. Food stories travel further than food itself." },
  { slug: "travel",          label: "Travel",              icon: "✈",  color: "#0284C7", tint: "#F0F9FF",
    blurb: "Not the guidebook. The moment. The neighbourhood. The person who changed your route. Tell us one." },
  { slug: "nature",          label: "Nature",              icon: "🌿", color: "#10B981", tint: "#ECFDF5",
    blurb: "A tree you know by name. A river you swim in. The season your city forgets. Nature is a subject, not a background." },
  { slug: "science",         label: "Science",             icon: "🔬", color: "#7C3AED", tint: "#F5F3FF",
    blurb: "Curious research, kitchen experiments, citizen astronomy. Explain something you love in plain language." },
  { slug: "weather",         label: "Weather",             icon: "⛅", color: "#0EA5E9", tint: "#F0F9FF",
    blurb: "Monsoons, blizzards, the day the sky went orange. Local weather memory is worth documenting — climate is downstream of stories." },
  { slug: "farmers",         label: "Farmers",             icon: "🌾", color: "#65A30D", tint: "#F7FEE7",
    blurb: "Notes from the field. Crop years, market prices, water, seeds, cooperatives. Farmers are journalists of the land." },
  { slug: "alumni",          label: "Alumni",              icon: "🎓", color: "#4F46E5", tint: "#EEF2FF",
    blurb: "Where did your batch scatter? What did school give you that you only understood later? Write it for the ones behind you." },
  { slug: "family-trees",    label: "Family Trees",        icon: "🌳", color: "#166534", tint: "#F0FDF4",
    blurb: "One story per grandparent is a gift. Names, migrations, recipes, occupations, silences — start the family record." },
  { slug: "memorials",       label: "Memorials",           icon: "🕯", color: "#78350F", tint: "#FEFCE8",
    blurb: "A page for someone you loved. A memory that shouldn't fade. Grief and celebration deserve a public home too." },
  { slug: "homes",           label: "Homes & Real Estate", icon: "🏠", color: "#7C2D12", tint: "#FEF3E4",
    blurb: "Renting, buying, building, restoring. Neighbourhood profiles. What the listings don't tell you." },
  { slug: "family-heritage", label: "Family & Heritage",   icon: "👨‍👩‍👧", color: "#B45309", tint: "#FFFBEB",
    blurb: "Traditions, rituals, festivals, dialects, family recipes. The culture inside your home is worth publishing." },
  { slug: "community",       label: "Community",           icon: "🤝", color: "#0F766E", tint: "#F0FDFA",
    blurb: "Local heroes, volunteer projects, neighbourhood repairs. Communities become visible when someone writes about them." },
  { slug: "local-news",      label: "Local News",          icon: "📰", color: "#334155", tint: "#F1F5F9",
    blurb: "Council meetings, potholes, market days, elections. Cover your street the way a national paper covers a capital." },
  { slug: "arts-culture",    label: "Arts & Culture",      icon: "🎨", color: "#DB2777", tint: "#FDF2F8",
    blurb: "Local artists, tiny theatres, gigs, bookshops, murals. If it moved you, it deserves a page." },
  { slug: "history",         label: "History",             icon: "🏛", color: "#78350F", tint: "#FEF3E4",
    blurb: "A place, a person, a struggle. Recover a story that was almost lost — write with sources and heart." },
  { slug: "faith",           label: "Faith & Spirituality",icon: "🙏", color: "#9333EA", tint: "#FAF5FF",
    blurb: "Prayer, doubt, practice, community. Faith stories are honest when they're specific — not preachy." },
  { slug: "technology",      label: "Technology",          icon: "💻", color: "#1E293B", tint: "#F8FAFC",
    blurb: "How you use a tool, what broke, what you built. Explain tech to your parents — they read too." },
  { slug: "business",        label: "Business",            icon: "📈", color: "#0F766E", tint: "#F0FDFA",
    blurb: "Small business, side project, employer story, market changes. Business is people; write about the people." },
];

export function BlogTopics() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});   // { slug: n_articles }

  // Best-effort: probe how many published articles exist per topic so
  // we can show "Be the first ›" for empty ones. Non-blocking — failures
  // just skip the badge.
  useEffect(() => {
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        TOPIC_CARDS.map(async (t) => {
          try {
            const r = await fetch(`${BASE}/hub/topic/${encodeURIComponent(t.slug)}?limit=1`);
            if (!r.ok) return [t.slug, null];
            const d = await r.json();
            return [t.slug, (d.articles || []).length];
          } catch {
            return [t.slug, null];
          }
        }),
      );
      if (alive) setCounts(Object.fromEntries(entries));
    })();
    return () => { alive = false; };
  }, []);

  return (
    <BlogPage testid="blog-topics-page">
      <PageHead
        overline="Explore topics"
        title="Every Subject Has a Community"
        subtitle="Choose a topic and start writing — even a 200-word post seeds a whole community. Every topic below is looking for its first authoritative local voice."
      />

      <div
        className="mb-10 rounded-3xl p-6 md:p-8 border flex flex-col md:flex-row items-start md:items-center gap-4"
        style={{ borderColor: "rgba(15,118,110,0.15)", background: "linear-gradient(135deg,#F0FDFA 0%,#FFFFFF 100%)" }}
        data-testid="blog-topics-writer-cta"
      >
        <div className="w-12 h-12 rounded-full grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}>
          <PenLine className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
            Which topics do you know best?
          </h3>
          <p className="mt-1 text-slate-600 text-sm">
            Nature, food, farming, family history — everyone is an expert in something. Pick one and publish your first short piece today.
          </p>
        </div>
        <button
          onClick={() => navigate("/publisher/write")}
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white shrink-0"
          style={{ background: "#0F766E" }}
          data-testid="blog-topics-start-writing"
        >
          Start writing ›
        </button>
      </div>

      <section data-testid="blog-topics-grid">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOPIC_CARDS.map((t) => {
            const n = counts[t.slug];
            const isEmpty = n === 0;
            return (
              <div
                key={t.slug}
                className="rounded-2xl p-5 border transition-all hover:-translate-y-0.5 hover:shadow-lg group relative overflow-hidden"
                style={{ borderColor: "rgba(15,23,42,0.06)", background: t.tint }}
                data-testid={`blog-topic-card-${t.slug}`}
              >
                {/* Corner glyph */}
                <div
                  className="absolute -top-4 -right-3 text-6xl opacity-15 select-none pointer-events-none"
                  style={{ color: t.color, fontFamily: "'Lora', serif" }}
                  aria-hidden="true"
                >
                  {t.icon}
                </div>

                <div
                  className="w-11 h-11 rounded-xl grid place-items-center text-xl mb-3 relative z-10"
                  style={{ background: t.color, color: "#ffffff", boxShadow: `0 4px 12px ${t.color}33` }}
                >
                  {t.icon}
                </div>
                <h3
                  className="text-xl relative z-10"
                  style={{ fontFamily: "'Lora', serif", fontWeight: 600, color: "#0F172A" }}
                >
                  {t.label}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed relative z-10">
                  {t.blurb}
                </p>

                <div className="mt-4 flex items-center justify-between gap-3 relative z-10">
                  <a
                    href={`/blog/topics/${t.slug}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-950 uppercase tracking-widest"
                    data-testid={`blog-topic-read-${t.slug}`}
                  >
                    {isEmpty ? "Be the first ›" : n ? `Read ${n} stor${n === 1 ? "y" : "ies"} ›` : "Explore ›"}
                  </a>
                  <button
                    onClick={() => navigate(`/publisher/write?topic=${encodeURIComponent(t.slug)}`)}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: t.color }}
                    data-testid={`blog-topic-write-${t.slug}`}
                  >
                    Write about {t.label.split(" ")[0]} ›
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA — soft invitation to suggest new topics */}
      <div
        className="mt-12 rounded-2xl border p-6 text-center"
        style={{ borderColor: "rgba(15,23,42,0.06)", background: "#ffffff" }}
        data-testid="blog-topics-suggest"
      >
        <p className="text-sm text-slate-600">
          Don't see a topic you want to write about?{" "}
          <a href="/blog/topics/suggest" className="font-semibold" style={{ color: "#0F766E" }}>
            Suggest a new one ›
          </a>
        </p>
      </div>
    </BlogPage>
  );
}


// ─── /blog/writers ───────────────────────────────────────────────

export function BlogWriters() {
  return (
    <BlogPage testid="blog-writers-page">
      <PageHead
        overline="Writers"
        title="Voices worth following"
        subtitle="Local writers, subject experts, community organizations, teachers, farmers, travelers, historians, students and seniors. Not ranked by follower count — ranked by helpfulness, originality and community trust."
      />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <Users className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Become a writer</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Sign up, tell us the places and topics you know, and start publishing. Your work reaches readers across the whole SocialPulse network.
          </p>
          <a href="/publisher/write" className="mt-4 inline-flex items-center gap-1 font-semibold" style={{ color: "#0F766E" }}>
            Start writing <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <Newspaper className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Featured writers</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Curated writer showcase pages are on the roadmap. Once a writer has 3+ published stories with community reactions, they'll appear here.
          </p>
          <p className="mt-3 text-[11px] text-slate-400 uppercase tracking-widest">Coming soon</p>
        </div>
      </div>
    </BlogPage>
  );
}
